import type { QueryClient } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { pendingKeys } from '../../pending';
import { requireSuccess } from '../../shared';
import { recordKeys } from './queries';
import { addLabelsToRecord, deleteRecords, removeLabelsFromRecord, updateRecord, updateRecords } from './recordsApi';

// A pending record is a record that hasn't started yet, so an edit can land in
// either list. Refresh both; only the ones currently on screen actually refetch.
//
// Not awaited (and not returned from onSuccess) on purpose: that would hold the
// caller's own onSuccess, i.e. the "saved" snackbar, until the lists had refetched.
const refreshRecordLists = (queryClient: QueryClient) => {
    queryClient.invalidateQueries({ queryKey: recordKeys.lists() });
    queryClient.invalidateQueries({ queryKey: pendingKeys.lists() });
};

interface UpdateRecordsVariables {
    deviceIds: string[];
    indexes: number[];
    record: Record<string, unknown>;
}

/** Applies the same config (metadata, thresholds, alerts…) to one or more records. */
export const useUpdateRecords = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ deviceIds, indexes, record }: UpdateRecordsVariables) =>
            requireSuccess(await updateRecords(deviceIds, indexes, record)),
        onSuccess: () => refreshRecordLists(queryClient),
    });
};

interface UpdateNoteVariables {
    deviceId: string;
    index: number;
    note: string;
}

const withNote =
    ({ deviceId, index, note }: UpdateNoteVariables) =>
    (records: any[] | undefined) =>
        records?.map((record) =>
            record.device_id === deviceId && record.index === index
                ? { ...record, meta_data: { ...record.meta_data, note } }
                : record
        );

/**
 * Saves a record's note, showing it in every table immediately: the cached record
 * is patched before the request goes out and rolled back if the server rejects it.
 */
export const useUpdateRecordNote = () => {
    const queryClient = useQueryClient();
    const lists = [recordKeys.lists(), pendingKeys.lists()];

    return useMutation({
        mutationFn: async ({ deviceId, index, note }: UpdateNoteVariables) =>
            requireSuccess(await updateRecord(deviceId, index, { note })),
        onMutate: async (variables) => {
            // Stop an in-flight refetch from landing on top of the patch with the old note.
            await Promise.all(lists.map((queryKey) => queryClient.cancelQueries({ queryKey })));
            const snapshots = lists.flatMap((queryKey) => queryClient.getQueriesData<any[]>({ queryKey }));
            lists.forEach((queryKey) => queryClient.setQueriesData<any[]>({ queryKey }, withNote(variables)));
            return { snapshots };
        },
        onError: (_error, _variables, context) => {
            context?.snapshots.forEach(([queryKey, data]) => queryClient.setQueryData(queryKey, data));
        },
        onSettled: () => refreshRecordLists(queryClient),
    });
};

/** Deletes records, each given as "<deviceId> <index>". */
export const useDeleteRecords = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (deviceIndexes: string[]) => requireSuccess(await deleteRecords(deviceIndexes)),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: recordKeys.lists() });
        },
    });
};

interface RecordLabelsVariables {
    deviceId: string;
    index: number;
    add?: string[];
    remove?: string[];
}

// The list caches hold raw records, whose label ids have come under three spellings.
const recordLabelIds = (record: any): string[] =>
    record.label_ids ?? record.labelIds ?? (record.labels || []).map((label: any) => label?.id ?? label?.label_id);

export const withLabelChanges =
    ({ deviceId, index, add = [], remove = [] }: RecordLabelsVariables) =>
    (records: any[] | undefined) =>
        records?.map((record) => {
            if (record.device_id !== deviceId || record.index !== index) return record;
            const kept = recordLabelIds(record).filter((id) => !remove.includes(id));
            return { ...record, label_ids: [...kept, ...add.filter((id) => !kept.includes(id))] };
        });

/** Attaches and detaches labels on a record, showing it in every table at once and reverting if it fails. */
export const useUpdateRecordLabels = () => {
    const queryClient = useQueryClient();
    const lists = [recordKeys.lists(), pendingKeys.lists()];

    return useMutation({
        mutationFn: async ({ deviceId, index, add = [], remove = [] }: RecordLabelsVariables) => {
            if (add.length) requireSuccess(await addLabelsToRecord(deviceId, index, add));
            if (remove.length) requireSuccess(await removeLabelsFromRecord(deviceId, index, remove));
        },
        onMutate: async (variables) => {
            await Promise.all(lists.map((queryKey) => queryClient.cancelQueries({ queryKey })));
            const snapshots = lists.flatMap((queryKey) => queryClient.getQueriesData<any[]>({ queryKey }));
            lists.forEach((queryKey) => queryClient.setQueriesData<any[]>({ queryKey }, withLabelChanges(variables)));
            return { snapshots };
        },
        onError: (_error, _variables, context) => {
            context?.snapshots.forEach(([queryKey, data]) => queryClient.setQueryData(queryKey, data));
        },
        onSettled: () => refreshRecordLists(queryClient),
    });
};
